import { peekReferralCode } from "./referralCapture";
// UTM / campaign attribution capture — pure, React-free so it runs at app
// startup (main.tsx) before React mounts and for LOGGED-OUT landing visitors.
//
// A marketing link (https://trybeup.com/?utm_source=facebook&utm_campaign=launch
// &utm_content=day_three_wins) is opened by brand-new people who then sign up.
// We stash the FIRST-TOUCH campaign params so they survive the logged-out →
// signup → authenticated round-trip; trackEvent then tags every event with the
// channel (PostHog + analytics_events), and the sign-up itself is attributed
// server-side from utmSignupMetadata / signupAttributionPayload below — so the
// admin Launch Funnel + PostHog can attribute a signup, and downstream
// activation, back to the channel and even the specific video (utm_content).
//
// We deliberately DO NOT strip the params from the URL (unlike the coach code):
// PostHog's autocapture reads utm_* off the pageview it fires on init, which
// happens after this runs.

export const UTM_STORAGE_KEY = "trybeup_utm";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
// Ad-click ids — handy for reconciling against the ad platforms later.
const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid"] as const;

export interface StoredUtm {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  capturedAt?: string;
}

/**
 * Read utm_* / click-ids from the current URL and stash them (first-touch:
 * never overwrite an existing capture). Idempotent; safe to call anywhere.
 */
export function captureUtmFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const found: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v && v.trim()) found[k] = v.trim().slice(0, 120);
    }
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k);
      if (v && v.trim()) found[k] = v.trim().slice(0, 250);
    }
    if (Object.keys(found).length === 0) return;
    // First-touch attribution — the channel that first brought them wins.
    if (localStorage.getItem(UTM_STORAGE_KEY)) return;
    found.capturedAt = new Date().toISOString();
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
  } catch {
    /* ignore — attribution is best-effort */
  }
}

/** The stored first-touch UTM, or null. */
export function getStoredUtm(): StoredUtm | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredUtm) : null;
  } catch {
    return null;
  }
}

/** A Meta click id as Meta issues it — URL-safe characters only, so one can
 *  never carry free text into a link or a cookie. */
const FBCLID_FORMAT = /^[A-Za-z0-9_-]{1,255}$/;
export function validFbclid(value: string | null | undefined): string | null {
  return value && FBCLID_FORMAT.test(value) ? value : null;
}

/**
 * `url` with this browser's stored first-touch campaign (utm_*) and Meta click
 * id (fbclid) added — for a link that opens in ANOTHER browser: the email
 * confirmation link, and the hand-off link copied out of a social app's
 * in-app browser. That browser has none of this one's storage, so the link
 * itself carries them; main.tsx's captureUtmFromUrl reads them back off the
 * address there. Two things then work that otherwise break at the browser
 * switch: events in the new browser are tagged with the channel (instead of
 * "direct"), and the Meta Pixel can rebuild its `_fbc` click cookie so the
 * sign-up conversion is credited to the ad, not just counted (lib/metaPixel.ts;
 * the Pixel task of 2026-10-08 flagged this as the known gap). Unchanged when
 * nothing is stored.
 */
export function withCarriedAttribution(url: string, stored: StoredUtm | null = getStoredUtm()): string {
  // A friend's referral code rides along too (App.tsx re-captures ?ref= on
  // any route; review 2026-10-21).
  const ref = peekReferralCode();
  if (!stored && !ref) return url;
  try {
    const u = new URL(url);
    for (const k of UTM_KEYS) {
      const v = stored?.[k];
      if (v) u.searchParams.set(k, v);
    }
    const fbclid = validFbclid(stored?.fbclid);
    if (fbclid) u.searchParams.set("fbclid", fbclid);
    if (ref) u.searchParams.set("ref", ref);
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * A compact channel tag to merge into an analytics event's metadata — only the
 * fields that matter for the funnel, only when present. Empty object if no UTM
 * was captured (organic/direct), so it never adds noise.
 */
export function channelTag(): Record<string, string> {
  const u = getStoredUtm();
  if (!u) return {};
  const tag: Record<string, string> = {};
  if (u.utm_source) tag.utm_source = u.utm_source;
  if (u.utm_medium) tag.utm_medium = u.utm_medium;
  if (u.utm_campaign) tag.utm_campaign = u.utm_campaign;
  if (u.utm_content) tag.utm_content = u.utm_content;
  return tag;
}

/**
 * The first-touch campaign + device, as sign-up form metadata. Spread into
 * GoTrue `signUp`'s `options.data` (→ user_metadata, which rides in the access
 * token) so `backend/services/signup_attribution.py` can record the channel
 * when the account row is created — even if the confirmation link opens in a
 * browser that never saw the ad. Same path the pending Pilot problem takes.
 * Ad-click ids are left out on purpose: the server doesn't keep them.
 */
export function utmSignupMetadata(platform: string): Record<string, string> {
  return { ...firstTouchFields(), signup_platform: platform };
}

function firstTouchFields(): Record<string, string> {
  const u = getStoredUtm();
  return { ...channelTag(), ...(u?.utm_term ? { utm_term: u.utm_term } : {}) };
}

const ATTRIBUTION_SENT_KEY = "trybeup_signup_attr_sent";
// Matches ATTRIBUTION_WINDOW in backend/services/signup_attribution.py — past
// it the server ignores the report, so don't send it.
const ATTRIBUTION_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Whether this device should report its stored first-touch UTM for this
 * account: a new account (created in the last 48h) that this device hasn't
 * reported for yet. The server makes the real "has none recorded" decision;
 * this just keeps it to one call per account per device.
 */
export function shouldSendSignupAttribution(
  userId: string,
  createdAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!userId || !createdAt) return false;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created) || now - created > ATTRIBUTION_WINDOW_MS) return false;
  try {
    return localStorage.getItem(ATTRIBUTION_SENT_KEY) !== userId;
  } catch {
    return false;
  }
}

export function markSignupAttributionSent(userId: string): void {
  try { localStorage.setItem(ATTRIBUTION_SENT_KEY, userId); } catch { /* ignore */ }
}

/** The body for POST /api/analytics/signup-attribution. */
export function signupAttributionPayload(platform: string): Record<string, string> {
  return { ...firstTouchFields(), platform };
}

// useAuth() is a plain hook mounted by many components, so its effects run
// once per mounted instance — guard so one account sends one request.
let attributionInFlight: string | null = null;

/**
 * Send this device's first-touch attribution for a new account, once.
 * `post` is injected (the authenticated api client) so this file stays
 * React- and network-free for main.tsx and tests. Marks the account as sent
 * only on success, so a failed call (offline, profile gate) retries next load.
 */
export function syncSignupAttribution(
  userId: string,
  createdAt: string | null | undefined,
  platform: string,
  post: (body: Record<string, string>) => Promise<unknown>,
): void {
  if (attributionInFlight === userId) return;
  if (!shouldSendSignupAttribution(userId, createdAt)) return;
  attributionInFlight = userId;
  post(signupAttributionPayload(platform))
    .then(() => markSignupAttributionSent(userId))
    .catch(() => { /* best-effort — retried on the next load */ })
    .finally(() => { attributionInFlight = null; });
}
